import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Button,
    Chip,
    Divider,
    useMediaQuery,
    useTheme,
    CircularProgress,
    IconButton
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    EventAvailable as AmenityIcon,
    ChevronRight as SeeAllIcon,
    People as PeopleIcon,
    AccessTime as AccessTimeIcon,
    Place as PlaceIcon,
    NotificationsActive as ActivityIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { coachingSessionAPI, coachingProgramAPI, classAPI, amenityAPI, coachAPI } from '../../services/api';
import { parseDateOnlyString, formatTimeString } from '../../utils/dateUtils';
import { CoachingSession, CoachingProgram } from '../../types/coaching';

// Data interfaces
interface ScheduleItem {
    id: string;
    title: string;
    level: string;
    date: string;
    time: string;
    enrollments: number;
    capacity: number;
    type: 'class' | 'session' | 'program';
    venue?: string;
}

interface AmenityBooking {
    id: string;
    amenityId: string;
    amenityName: string;
    date: string;
    time: string;
    duration: number;
    status: 'confirmed' | 'pending' | 'cancelled';
}

interface RecentActivity {
    id: string;
    memberName: string;
    action: 'registered' | 'cancelled';
    className: string;
    timestamp: string;
}

const CoachDashboard: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State for dashboard data
    const [mySchedule, setMySchedule] = useState<ScheduleItem[]>([]);
    const [myBookings, setMyBookings] = useState<AmenityBooking[]>([]);
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        if (!user?.id) return;

        try {
            setLoading(true);

            // Get current date and 2 weeks ahead for filtering
            const now = new Date();
            const twoWeeksAhead = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
            const startDate = now.toISOString().split('T')[0];
            const endDate = twoWeeksAhead.toISOString().split('T')[0];

            // Fetch coaching sessions for this coach
            const sessionsResponse = await coachingSessionAPI.getCoachingSessions({
                coachId: user.id,
                startDate,
                endDate
            });

            // Fetch coaching programs for this coach
            const programsResponse = await coachingProgramAPI.getCoachingPrograms({
                coachId: user.id,
                startDate,
                endDate
            });

            // Fetch classes for this coach (if they coach classes too)
            const classesResponse = await classAPI.getClasses({
                coachId: user.id,
                startDate,
                endDate
            });

            // Combine and format schedule data
            const scheduleItems: ScheduleItem[] = [];

            // Add coaching sessions
            if (sessionsResponse.data) {
                sessionsResponse.data.forEach((session: CoachingSession) => {
                    scheduleItems.push({
                        id: session.id,
                        title: session.name,
                        level: session.level,
                        date: session.sessionDate,
                        time: session.startTime,
                        enrollments: 0, // TODO: Get actual booking count
                        capacity: session.capacity,
                        type: 'session',
                        venue: session.venues && session.venues.length > 0 ? `${session.venues.length} venue(s)` : 'Not specified'
                    });
                });
            }

            // Add coaching programs
            if (programsResponse.data) {
                programsResponse.data.forEach((program: CoachingProgram) => {
                    // Try to get time from sessionTime or recurringPattern.startTime
                    const programTime = program.sessionTime || program.recurringPattern?.startTime || '';
                    
                    scheduleItems.push({
                        id: program.id,
                        title: program.name,
                        level: program.level,
                        date: program.startDate,
                        time: programTime,
                        enrollments: program.currentEnrollments,
                        capacity: program.capacity,
                        type: 'program',
                        venue: program.venues && program.venues.length > 0 ? `${program.venues.length} venue(s)` : 'Not specified'
                    });
                });
            }

            // Add classes (if any)
            if (classesResponse.data) {
                classesResponse.data.forEach((classItem: any) => {
                    scheduleItems.push({
                        id: classItem.id,
                        title: classItem.name,
                        level: classItem.level || 'All Levels',
                        date: classItem.date,
                        time: classItem.startTime,
                        enrollments: classItem.currentBookings || 0,
                        capacity: classItem.capacity,
                        type: 'class',
                        venue: classItem.venue
                    });
                });
            }

            // Sort by date and time
            scheduleItems.sort((a, b) => {
                const dateA = new Date(`${a.date}T${a.time}`);
                const dateB = new Date(`${b.date}T${b.time}`);
                return dateA.getTime() - dateB.getTime();
            });

            setMySchedule(scheduleItems);

            // Fetch amenity reservations for this coach
            try {
                const amenityBookingsResponse = await amenityAPI.getCoachReservations();

                const bookings: AmenityBooking[] = amenityBookingsResponse.data
                    .filter((booking: any) => {
                        // Filter to only show bookings within the date range
                        const bookingDate = new Date(booking.bookingDate);
                        const start = new Date(startDate);
                        const end = new Date(endDate);
                        return bookingDate >= start && bookingDate <= end;
                    })
                    .map((booking: any) => ({
                        id: booking.id,
                        amenityId: booking.amenity?.id || '',
                        amenityName: booking.amenity?.name || 'Unknown Amenity',
                        date: booking.bookingDate,
                        time: booking.startTime,
                        duration: (() => {
                            // Calculate duration from start and end times
                            if (booking.startTime && booking.endTime) {
                                const [startHour, startMin] = booking.startTime.split(':').map(Number);
                                const [endHour, endMin] = booking.endTime.split(':').map(Number);
                                return (endHour * 60 + endMin) - (startHour * 60 + startMin);
                            }
                            return 60; // Default 60 minutes
                        })(),
                        status: booking.status?.toLowerCase() || 'confirmed'
                    }));

                console.log('[COACH DASHBOARD] Filtered bookings:', bookings.length);

                // Sort by date and time
                bookings.sort((a, b) => {
                    const dateA = new Date(`${a.date}T${a.time}`);
                    const dateB = new Date(`${b.date}T${b.time}`);
                    return dateA.getTime() - dateB.getTime();
                });

                console.log('[COACH DASHBOARD] Final bookings to display:', bookings);
                setMyBookings(bookings);
            } catch (error) {
                console.error('[COACH DASHBOARD] Error fetching amenity bookings:', error);
                setMyBookings([]);
            }

            // Fetch recent activity for this coach
            try {
                const activityResponse = await coachAPI.getCoachActivity(user.id, { limit: 10 });
                setRecentActivity(activityResponse.data.activities || []);
            } catch (error) {
                console.warn('Could not fetch coach activity:', error);
                setRecentActivity([]);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setLoading(false);

            // Set empty arrays on error
            setMySchedule([]);
            setMyBookings([]);
            setRecentActivity([]);
        }
    };

    const getPercentageFull = (enrollments: number, capacity: number): number => {
        return Math.round((enrollments / capacity) * 100);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed': return 'success';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getActionColor = (action: string) => {
        return action === 'registered' ? 'success' : 'error';
    };

    const getEnrollmentColor = (percentage: number): string => {
        if (percentage < 50) return '#ffcdd2'; // Pastel red
        if (percentage >= 50 && percentage < 80) return '#fff3c4'; // Pastel yellow
        return '#c8e6c9'; // Pastel green
    };

    const getEnrollmentTextColor = (percentage: number): string => {
        if (percentage < 50) return '#d32f2f'; // Red text
        if (percentage >= 50 && percentage < 80) return '#f57c00'; // Orange text
        return '#388e3c'; // Green text
    };

    if (loading) {
        return (
            <Container>
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h4" gutterBottom>
                        Loading...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', width: '100%' }}>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Welcome, Coach {user?.firstName}!
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Manage your coaching schedule and bookings
                </Typography>
            </Paper>

            <Grid container spacing={3}>
                {/* My Schedule */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 400
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">
                                My Schedule ({mySchedule.length})
                            </Typography>
                            <Box>
                                <IconButton size="small" onClick={fetchDashboardData} disabled={loading}>
                                    <RefreshIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}

                        {!loading && mySchedule.length === 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    No upcoming sessions scheduled
                                </Typography>
                            </Box>
                        )}

                        {!loading && mySchedule.length > 0 && (
                            <Box sx={{ flexGrow: 1, maxHeight: 400, overflow: 'auto' }}>
                                <List sx={{ p: 0 }}>
                                    {mySchedule.map((item, index) => {
                                        const enrollmentPercentage = getPercentageFull(item.enrollments, item.capacity);
                                        // Use parseDateOnlyString to avoid UTC conversion issues
                                        const itemDate = parseDateOnlyString(item.date);
                                        const formattedDate = itemDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                        
                                        // Format time using formatTimeString utility
                                        const formattedTime = item.time ? formatTimeString(item.time) : '';

                                        return (
                                            <React.Fragment key={item.id}>
                                                <ListItem
                                                    sx={{
                                                        px: 2,
                                                        py: 1.5,
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        borderRadius: 1
                                                    }}
                                                    onClick={() => {
                                                        if (item.type === 'session') navigate(`/coaching/sessions/${item.id}`);
                                                        else if (item.type === 'program') navigate(`/coaching/programs/${item.id}`);
                                                        else navigate(`/classes/${item.id}`, { state: { from: 'dashboard' } });
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mr: 2,
                                                            minWidth: 60
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                textAlign: 'center',
                                                                borderRight: '2px solid',
                                                                borderColor: 'primary.main',
                                                                pr: 2
                                                            }}
                                                        >
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                                                                {formattedDate.split(' ')[1]}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                {formattedDate.split(' ')[0]}
                                                            </Typography>
                                                            {formattedTime && (
                                                                <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 600, mt: 0.5 }}>
                                                                    {formattedTime}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                    {item.title}
                                                                </Typography>
                                                                <Chip 
                                                                    label={item.type.toUpperCase()} 
                                                                    size="small"
                                                                    sx={{ 
                                                                        fontSize: '0.7rem',
                                                                        height: 20,
                                                                        bgcolor: item.type === 'class' ? 'info.light' : item.type === 'session' ? 'success.light' : 'warning.light'
                                                                    }}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                                    {item.venue && (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <PlaceIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {item.venue}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                bgcolor: getEnrollmentColor(enrollmentPercentage),
                                                                                px: 1,
                                                                                py: 0.25,
                                                                                borderRadius: 1
                                                                            }}
                                                                        >
                                                                            <PeopleIcon sx={{
                                                                                fontSize: 15,
                                                                                color: getEnrollmentTextColor(enrollmentPercentage)
                                                                            }} />
                                                                            <Typography 
                                                                                variant="caption" 
                                                                                sx={{ 
                                                                                    fontWeight: 600,
                                                                                    color: getEnrollmentTextColor(enrollmentPercentage)
                                                                                }}
                                                                            >
                                                                                {item.enrollments}/{item.capacity} ({enrollmentPercentage}%)
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        }
                                                        secondaryTypographyProps={{ component: 'div' }}
                                                    />
                                                </ListItem>
                                                {index < mySchedule.length - 1 && <Divider />}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* My Bookings */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 400
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">
                                My Bookings ({myBookings.length})
                            </Typography>
                            <Box>
                                <IconButton size="small" onClick={fetchDashboardData} disabled={loading}>
                                    <RefreshIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}

                        {!loading && myBookings.length === 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <AmenityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    No upcoming amenity bookings
                                </Typography>
                            </Box>
                        )}

                        {!loading && myBookings.length > 0 && (
                            <Box sx={{ flexGrow: 1, maxHeight: 400, overflow: 'auto' }}>
                                <List sx={{ p: 0 }}>
                                    {myBookings.map((booking, index) => {
                                        // Use parseDateOnlyString to avoid UTC conversion issues
                                        const bookingDate = parseDateOnlyString(booking.date);
                                        const formattedDate = bookingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                        const formattedTime = booking.time ? formatTimeString(booking.time) : '';

                                        return (
                                            <React.Fragment key={booking.id}>
                                                <ListItem
                                                    sx={{
                                                        px: 2,
                                                        py: 1.5,
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        borderRadius: 1
                                                    }}
                                                    onClick={() => navigate(`/amenities/${booking.amenityId}/bookings/${booking.id}`)}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mr: 2,
                                                            minWidth: 60
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                textAlign: 'center',
                                                                borderRight: '2px solid',
                                                                borderColor: 'primary.main',
                                                                pr: 2
                                                            }}
                                                        >
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                                                                {formattedDate.split(' ')[1]}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formattedDate.split(' ')[0]}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                    {booking.amenityName}
                                                                </Typography>
                                                                <Chip
                                                                    label={booking.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                    size="small"
                                                                    color={getStatusColor(booking.status) as any}
                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <AccessTimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formattedTime}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <AccessTimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {booking.duration} min
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        }
                                                        secondaryTypographyProps={{ component: 'div' }}
                                                    />
                                                </ListItem>
                                                {index < myBookings.length - 1 && <Divider />}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default CoachDashboard;